import { TileCoordinates } from "../../../../lib/runescape/coordinates";
import Behaviour, { SingleBehaviour } from "../../../../lib/ui/Behaviour";
import { lazy } from "../../../../lib/Lazy";
import * as leaflet from "leaflet";
import { AdaptiveScanRadiusMarker, ScanRegionPolygon } from "../../../cluesolving/ScanLayer";
import { PathEditor } from "../../../pathedit/PathEditor";
import { OpacityGroup } from "../../../../lib/gamemap/layers/OpacityLayer";
import { ewent, observe } from "../../../../lib/reactive";
import { InteractionGuard } from "../../../../lib/gamemap/interaction/InteractionLayer";
import { GameMapControl } from "../../../../lib/gamemap/GameMapControl";
import ScanTools from "./ScanTools";
import { C } from "../../../../lib/ui/constructors";
import { ScanTree } from "../../../cluetheory/scans/ScanTree";
import TreeEdit from "./TreeEdit";
import MethodSubEditor from "../MethodSubEditor";
import lodash from "lodash";
import { PathStepEntity } from "../../map/entities/PathStepEntity";
import { GameLayer } from "../../../../lib/gamemap/GameLayer";
import { MapEntity } from "../../../../lib/gamemap/MapEntity";
import { Rectangle } from "../../../../lib/math";
import { Scans } from "../../../cluetheory/Scans";
import { levelIcon } from "../../../../lib/gamemap/GameMap";
import Properties from "../../widgets/Properties";
import { TextRendering } from "../../TextRendering";
import { FormModal } from "../../../../lib/ui/controls/FormModal";
import NumberInput from "../../../../lib/ui/controls/NumberInput";
import { BigNisButton } from "../../widgets/BigNisButton";
import ControlWithHeader from "../../map/ControlWithHeader";
var AugmentedScanTree = ScanTree.Augmentation.AugmentedScanTree;
import { ClueTrainerWiki } from "../../../wiki";
import { ScanProfile } from "../../../cluetheory/scans/ScanProfile";
var ScanEquivalenceClasses = ScanProfile.ScanEquivalenceClasses;
import { LeafletUtils } from "../../../../lib/gamemap/LeafletUtils";
import { LeafletPolygonConstructors } from "../../../../lib/gamemap/LeafletPolygonConstructors";
var areaToPolygon = LeafletPolygonConstructors.areaToPolygon;
import { FakeLodash } from "../../../../lib/coreutil/FakeLodash";
export class ScanEditLayer extends GameLayer {
    constructor(spots) {
        super();
        this.spots = spots;
        this.markers = [];
        this.marker = new AdaptiveScanRadiusMarker().addTo(this);
        this.setSpots(spots);
    }
    setSpots(spots) {
        this.markers.forEach(m => {
            m.regular.remove();
            m.complement.remove();
        });
        this.markers = spots.map(s => new ScanEditLayer.MarkerPair(s));
        this.markers.forEach((m, i) => {
            m.regular.addTo(this);
            m.complement.addTo(this);
        });
        if (spots.length > 0)
            this.marker.setComplementByExampleSpot(spots[0]);
        return this;
    }
    setActiveCandidates(coords) {
        this.markers.forEach(m => m.setActive(coords.some(c => TileCoordinates.eq2(c, m.spot))));
    }
    getMarker(coords) {
        return this.markers.find(m => TileCoordinates.eq2(m.spot, coords));
    }
    setSpotOrder(order) {
        if (order) {
            order.forEach((spot, i) => {
                this.getMarker(spot)?.setNumber(i + 1);
            });
        }
        else {
            this.markers.forEach(m => m.setNumber(null));
        }
    }
    setTiming(timing) {
        this.markers.forEach(m => {
            m.setTiming(timing.spots.find(t => TileCoordinates.eq(t.spot, m.spot)));
        });
    }
}
(function (ScanEditLayer) {
    var render_digspot = TextRendering.render_digspot;
    var complementSpot = Scans.complementSpot;
    var span = C.span;
    var inlineimg = C.inlineimg;
    var cls = C.cls;
    class MarkerPair {
        constructor(spot) {
            this.spot = spot;
            this.regular = new ScanEditLayer.SpotMarker(spot, false);
            this.complement = new ScanEditLayer.SpotMarker(spot, true);
        }
        setNumber(n) {
            this.regular.setNumber(n);
            this.complement.setNumber(n);
        }
        setActive(v) {
            const opacity = v ? 1 : 0.2;
            this.regular.opacity.set(opacity);
            this.complement.opacity.set(opacity);
        }
        setTiming(timing) {
            this.regular.setTiming(timing);
            this.complement.setTiming(timing);
        }
    }
    ScanEditLayer.MarkerPair = MarkerPair;
    class SpotMarker extends MapEntity {
        constructor(spot, is_complement) {
            super();
            this.spot = spot;
            this.is_complement = is_complement;
            this.spot_on_map = null;
            this.number = undefined;
            this.setInteractive();
            this.spot_on_map = is_complement ? complementSpot(spot) : spot;
            this.setTooltip(async () => {
                const props = new Properties();
                if (this.is_complement) {
                    props.header(c().append("Complement of Spot ", render_digspot(this.number)));
                }
                else {
                    props.header(c().append("Spot ", render_digspot(this.number)));
                }
                if (this.is_complement) {
                    props.row(cls("ctr-step-properties-explanation")
                        .append(inlineimg("/assets/icons/info.png"), " This is the complement of a dig spot. Right click to learn more about complement spots."));
                }
                if (this.timing_information) {
                    let timing = c();
                    this.timing_information.timings.forEach((t, i) => {
                        if (i != 0)
                            timing.append(" | ");
                        let s = span(t.ticks.toFixed(2) + " ticks");
                        if (t.incomplete)
                            s.css("color", "yellow").tooltip("Incomplete path");
                        timing.append(s);
                    });
                    const any_incomplete = this.timing_information.timings.some(t => t.incomplete);
                    if (this.timing_information.timings.length > 1) {
                        const avg = span(this.timing_information.statistics.average.toFixed(2) + " ticks");
                        if (any_incomplete)
                            avg.css("color", "yellow").tooltip("Incomplete path");
                        timing.append(", Average ", avg);
                    }
                    props.named("Expected time", timing);
                    if (any_incomplete) {
                        props.row(c().css("font-style", "italic").text("Timings in yellow are from incomplete branches."));
                    }
                }
                return props;
            });
        }
        bounds() {
            return Rectangle.from(this.spot_on_map);
        }
        async render_implementation(props) {
            const marker = leaflet.marker(LeafletUtils.latLongFromVector2(this.spot_on_map), {
                icon: levelIcon(this.spot.level, props.highlight ? 1.5 : 1),
                opacity: props.opacity
            }).addTo(this);
            if (this.number) {
                marker.bindTooltip(leaflet.tooltip({
                    content: this.number.toString(),
                    className: "spot-number-on-map",
                    offset: [0, 10],
                    permanent: true,
                    direction: "center",
                    opacity: props.opacity
                }));
            }
            return marker.getElement();
        }
        setNumber(n) {
            this.number = n;
            this.render(true);
        }
        setTiming(timing) {
            this.timing_information = timing;
        }
        async contextMenu(event) {
            if (this.is_complement) {
                event.addForEntity({
                    type: "basic",
                    text: "About complement spots",
                    handler: () => {
                        ClueTrainerWiki.openOnPage("toofardifferentlevel");
                    }
                });
            }
            return {
                type: "submenu",
                text: () => c().append("Spot ", render_digspot(this.number)),
                children: []
            };
        }
    }
    ScanEditLayer.SpotMarker = SpotMarker;
})(ScanEditLayer || (ScanEditLayer = {}));
class EquivalenceClassHandling extends Behaviour {
    constructor(parent) {
        super();
        this.parent = parent;
        this.equivalence_classes = [];
    }
    render_equivalence_classes(ecs) {
        let layer = leaflet.featureGroup();
        ecs.equivalence_classes.map((c) => {
            let color = Math.abs(c.information_gain - ecs.max_information) < 0.01
                ? "blue"
                : `rgb(${255 * (1 - (c.information_gain / ecs.max_information))}, ${255 * c.information_gain / ecs.max_information}, 0)`;
            let polygon = leaflet.featureGroup();
            areaToPolygon(ecs.raster, (p) => p.id == c.id, c.area[0]).setStyle({
                color: "black",
                opacity: 1,
                weight: 3,
                fillOpacity: 0.25,
                fillColor: color
            }).addTo(polygon);
            leaflet.marker(polygon.getBounds().getCenter(), {
                icon: leaflet.divIcon({
                    iconSize: [50, 20],
                    className: "equivalence-class-information",
                    html: `${c.information_gain.toFixed(2)}b`,
                }),
            }).addTo(polygon);
            return polygon;
        }).forEach(p => layer.addLayer(p));
        return layer;
    }
    begin() {
        let self = this;
        function setup(o, visibility) {
            let options = observe(o);
            let layer = options.map(o => lazy(() => self.render_equivalence_classes(new ScanEquivalenceClasses(o))));
            layer.subscribe((l, old) => {
                if (old.hasValue())
                    self.parent.layer.removeLayer(old.get());
                if (visibility.value())
                    self.parent.layer.addLayer(l.get());
            });
            visibility.subscribe(v => {
                if (v)
                    self.parent.layer.addLayer(layer.value().get());
                else if (layer.value().hasValue())
                    self.parent.layer.removeLayer(layer.value().get());
            });
            return {
                options: options,
                layer: layer,
            };
        }
        let normal = setup({
            candidates: this.parent.value.clue.clue.spots,
            range: this.parent.builder.tree.assumed_range,
            complement: false,
            floor: this.parent.app.map.floor.value()
        }, this.parent.tools.normal);
        const complement = setup({
            candidates: this.parent.value.clue.clue.spots,
            range: this.parent.builder.tree.assumed_range,
            complement: true,
            floor: this.parent.app.map.floor.value()
        }, this.parent.tools.complement);
        this.equivalence_classes = [normal, complement];
        this.parent.app.map.floor
            .subscribe(f => this.equivalence_classes.forEach(t => t.options.update(o => o.floor = f)));
        this.parent.candidates_at_active_node
            .subscribe(cs => this.equivalence_classes.forEach(t => t.options.update(o => o.candidates = cs)));
    }
    end() {
        this.equivalence_classes.forEach(e => {
            if (e.layer.value().hasValue())
                e.layer.value().get().remove();
        });
        this.equivalence_classes = [];
    }
}
export class ScanTreeBuilder {
    constructor(clue) {
        this.clue = clue;
        this.tree = null;
        this.augmented = observe(null);
        this.preview_invalid = ewent();
        this.assumptions = observe({}).equality((a, b) => {
            return a.meerkats_active == b.meerkats_active
                && a.mobile_perk == b.mobile_perk
                && a.double_escape == b.double_escape
                && a.double_surge == b.double_surge;
        });
        this.order_changed = ewent();
        this.any_change = ewent();
        this.assumptions.subscribe((v) => {
            this.cleanTree();
        });
    }
    async cleanTree() {
        if (!this.tree)
            return;
        this.tree.assumed_range = this.clue.range + (this.assumptions.value().meerkats_active ? 5 : 0);
        this.augmented.set(await ScanTree.Augmentation.augment({
            augment_paths: true,
            analyze_completeness: true,
            analyze_correctness: true,
            analyze_timing: true,
            path_assumptions: this.assumptions.value()
        }, ScanTree.normalize(this.tree), this.clue));
        this.preview_invalid.trigger(null);
    }
    set(tree) {
        this.tree = tree;
        this.cleanTree();
    }
    setRegion(node, region) {
        this.updateNode(node, n => n.region = region);
    }
    updateNode(node, updater) {
        updater(node);
        this.cleanTree();
        this.any_change.trigger(null);
    }
    setPath(node, path) {
        this.updateNode(node, n => n.path = path);
    }
    setOrder(order) {
        this.tree.ordered_spots = order;
        this.order_changed.trigger(order);
    }
}
class PreviewLayerControl extends Behaviour {
    constructor(parent) {
        super();
        this.parent = parent;
        this.path_layer = null;
    }
    begin() {
        this.layer = new OpacityGroup().addTo(this.parent.layer);
        // Render path preview
        this.parent.tree_edit.active.subscribe(() => this.updatePreview(), true);
        this.parent.builder.preview_invalid.on(() => this.updatePreview());
    }
    async updatePreview() {
        let a = this.parent.tree_edit.active_node.value();
        let layer = new OpacityGroup();
        if (a) {
            for (const n of AugmentedScanTree.collect_parents(a, true)) {
                const area = ScanTree.getTargetRegion(n);
                if (area) {
                    this.parent.tree_edit.getNode(n).region_preview = new ScanRegionPolygon(area).addTo(layer);
                }
                if (n != a)
                    PathStepEntity.renderPath(n.raw.path).addTo(layer);
            }
        }
        else {
            if (this.parent.tree_edit.root_widget) {
                AugmentedScanTree.traverse(this.parent.tree_edit.root_widget.node, async (n) => {
                    const area = ScanTree.getTargetRegion(n);
                    if (area?.area) {
                        this.parent.tree_edit.getNode(n).region_preview = new ScanRegionPolygon(area).addTo(layer);
                    }
                    return PathStepEntity.renderPath(n.raw.path).addTo(layer);
                }, true);
            }
        }
        if (this.path_layer)
            this.path_layer.remove();
        this.path_layer = layer.addTo(this.layer);
    }
    end() { }
}
export default class ScanEditor extends MethodSubEditor {
    constructor(parent, app, value, side_panel) {
        super(parent);
        this.parent = parent;
        this.app = app;
        this.value = value;
        this.side_panel = side_panel;
        this.layer = undefined;
        this.builder = new ScanTreeBuilder(value.clue.clue);
        this.builder.assumptions.set(FakeLodash.cloneDeep(value.method.assumptions));
        this.builder.augmented.subscribe((t) => {
            this.layer.setTiming(t.state.timing_analysis);
        });
        this.builder.any_change.on(() => {
            this.parent.registerChange();
        });
        this.builder.order_changed.on((order) => {
            this.layer.setSpotOrder(order);
        });
        this.layer = new ScanEditLayer(value.clue.clue.spots);
        this.layer.marker.setClickable(true);
        const self = this;
        this.layer.add(new class extends GameLayer {
            eventContextMenu(event) {
                event.onPre(() => {
                    if (event.active_entity instanceof ScanEditLayer.SpotMarker) {
                        const spot = event.active_entity.spot;
                        event.addForEntity({
                            type: "basic",
                            text: "Set spot number",
                            handler: async () => {
                                const id = await (new class extends FormModal {
                                    constructor() {
                                        super({ size: "small" });
                                        this.title.set("Set spot number");
                                    }
                                    render() {
                                        super.render();
                                        const props = new Properties().appendTo(this.body);
                                        props.named("New Spot Number", this.input = new NumberInput(1, self.value.clue.clue.spots.length)
                                            .setValue(self.builder.tree.ordered_spots.findIndex(c => TileCoordinates.eq(c, spot)) + 1));
                                        this.input.raw().focus();
                                    }
                                    getButtons() {
                                        return [
                                            new BigNisButton("Cancel", "cancel").onClick(() => this.cancel()),
                                            new BigNisButton("Save", "confirm").onClick(() => this.confirm(this.input.get() - 1)),
                                        ];
                                    }
                                    getValueForCancel() {
                                        return undefined;
                                    }
                                }).do();
                                if (id !== undefined) {
                                    const old = lodash.clone(self.builder.tree.ordered_spots);
                                    const old_id = old.findIndex(c => TileCoordinates.eq(spot, c));
                                    const tmp = old[id];
                                    old[id] = old[old_id];
                                    old[old_id] = tmp;
                                    self.builder.setOrder(old);
                                }
                            }
                        });
                    }
                });
            }
        });
        this.interaction_guard = new InteractionGuard().setDefaultLayer(this.layer);
        this.equivalence_classes = this.withSub(new EquivalenceClassHandling(this));
        this.preview_layer = this.withSub(new PreviewLayerControl(this));
        this.path_editor = this.withSub(new SingleBehaviour());
        this.assumptions.subscribe((v) => {
            this.value.method.tree.assumed_range = this.value.clue.clue.range + (v.meerkats_active ? 5 : 0);
            this.builder.assumptions.set(FakeLodash.cloneDeep(v));
            this.layer.marker.setRadius(this.value.method.tree.assumed_range, v.meerkats_active);
        });
        this.builder.augmented.subscribe(a => {
            this.value.method.expected_time = a.state.timing_analysis.statistics.average + 1; // + 1 Tick because digging isn't included in the path analysis
        });
    }
    setPathEditor(node) {
        this.path_editor.set(new PathEditor(this.layer.marker, // This is a horrible hack to ensure correct event priority
        this.app.template_resolver, {
            initial: node.path.raw,
            target: node.path.target,
            start_state: node.path.pre_state,
            discard_handler: () => { },
            commit_handler: (p) => {
                this.builder.setPath(node.raw, p);
            }
        }, false)
            .onStop(() => {
            if (this.tree_edit.active_node.value() == node)
                this.tree_edit.requestActivation(null);
        }));
    }
    begin() {
        super.begin();
        this.layer.setSpotOrder(this.value.method.tree.ordered_spots);
        new GameMapControl({
            position: "top-right",
            type: "floating"
        }, new ControlWithHeader("Scan Tools")
            .setContent(this.tools = new ScanTools(this))).addTo(this.layer);
        this.builder.set(this.value.method.tree);
        c("<div style='font-weight: bold; text-align: center'>Scan Tree</div>").appendTo(this.side_panel);
        this.tree_edit = new TreeEdit(this, this.builder.tree.root)
            .css("overflow-y", "auto").appendTo(this.side_panel);
        this.candidates_at_active_node = this.tree_edit.active
            .map(n => n ? n.node.remaining_candidates : this.value.clue.clue.spots);
        this.candidates_at_active_node.subscribe(n => {
            this.layer.setActiveCandidates(n);
        });
        this.app.map.addGameLayer(this.layer);
        this.tree_edit.active_node.subscribe(async (node) => {
            if (node)
                this.setPathEditor(node);
            else
                this.path_editor.set(null);
        });
    }
    end() {
        this.layer.remove();
        this.tree_edit.remove();
    }
    setSpotOrder(order) {
        this.value.method.tree.ordered_spots;
    }
}
